import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import {
  MagnifyingGlass, FloppyDisk, CheckCircle, Sparkle, Lightbulb, Robot,
  ArrowsClockwise, WarningCircle,
} from "@phosphor-icons/react";

/**
 * Zoekprofiel — 28 velden in 8 secties. Slimme voor­invulling vanuit het
 * coachee-profiel, CV en werkwaardetest. Antwoorden worden automatisch
 * opgeslagen op blur (debounced).
 *
 * Op de eerste keer openen draait er een eenmalige AI-prefill die ALLES
 * meeneemt wat we van de coachee weten (CV, breintype, IQ, werkwaarden)
 * en zoveel mogelijk velden alvast invult.
 */
export default function CoacheeSearchProfilePage() {
  const [coachee, setCoachee] = useState(null);
  const [profile, setProfile] = useState(null);
  const [workValuesTop, setWorkValuesTop] = useState([]);
  const [draft, setDraft] = useState({});
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [aiRunning, setAiRunning] = useState(false);
  const [refillConfirm, setRefillConfirm] = useState(false);
  const [refilling, setRefilling] = useState(false);

  useAITaskRegistration({
    active: (aiRunning || refilling) && !!coachee,
    id: coachee ? `search-profile-${coachee.id}` : null,
    label: "Zoekprofiel invullen met AI",
    returnTo: "/zoekprofiel",
  });
  const saveTimer = useRef(null);
  const navigate = useNavigate();

  // Load coachee + profile + work values top-3 on mount, then trigger AI
  // prefill if this is the very first open AND we have some signal.
  useEffect(() => {
    (async () => {
      let initialDataLoaded = false;
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (!own) {
          setLoading(false);
          return;
        }
        const [sp, wv] = await Promise.allSettled([
          api.get(`/coachees/${own.id}/search-profile`),
          api.get(`/coachees/${own.id}/work-values`),
        ]);
        let initialProfile = null;
        if (sp.status === "fulfilled") {
          initialProfile = sp.value.data;
          setProfile(initialProfile);
          setDraft(initialProfile.data || {});
        }
        if (wv.status === "fulfilled" && wv.value.data?.state === "completed" && wv.value.data.result) {
          setWorkValuesTop(wv.value.data.result.slice(0, 3));
        }
        initialDataLoaded = true;
        // Stop the generic "Laden..." view so we can switch to the dedicated
        // sp-ai-running screen during the AI call.
        setLoading(false);
        if (initialProfile && initialProfile.ai_prefill_available) {
          setAiRunning(true);
          try {
            const { data: filled } = await api.post(
              `/coachees/${own.id}/search-profile/ai-prefill`,
              {},
              { timeout: 90000 }, // AI can take 20-40s — give it room
            );
            setProfile(filled);
            setDraft(filled.data || {});
          } catch (err) {
            console.warn("AI prefill failed:", err?.message || err);
            // Keep the simple-prefill profile we already loaded
          } finally {
            setAiRunning(false);
          }
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        if (!initialDataLoaded) setLoading(false);
      }
    })();
  }, []);

  const handleChange = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(() => persistDraft({ ...draft, [key]: value }), 700);
  };

  const persistDraft = async (data) => {
    if (!coachee) return;
    try {
      const { data: resp } = await api.put(`/coachees/${coachee.id}/search-profile`, { data });
      setProfile(resp);
      setSaveState("saved");
    } catch (err) {
      console.warn("Zoekprofiel save failed:", err?.message || err);
      setSaveState("idle");
    }
  };

  const handleBlur = () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      persistDraft(draft);
    }
  };

  const handleRefill = async () => {
    if (!coachee || refilling) return;
    setError("");
    setRefilling(true);
    setRefillConfirm(false);
    // Flush eventueel openstaande autosave eerst zodat user-edits zeker
    // bewaard zijn voordat de AI opnieuw draait.
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      try {
        await persistDraft(draft);
      } catch (err) {
        // Niet-fataal: de save heeft een eigen console.warn in persistDraft.
        // We loggen hier extra zodat dit pad zichtbaar is bij debugging.
        console.warn("Zoekprofiel pre-refill flush failed:", err?.message || err);
      }
    }
    try {
      const { data } = await api.post(
        `/coachees/${coachee.id}/search-profile/ai-prefill?force=true`,
        {},
        { timeout: 120000 },
      );
      setProfile(data);
      setDraft(data.data || {});
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefilling(false);
    }
  };

  const completeness = profile?.completeness;
  const prefillSources = profile?.prefill_sources || {};

  if (loading) {
    return <AppLayout title="Zoekprofiel"><div className="text-zinc-500">Laden...</div></AppLayout>;
  }
  if (aiRunning || refilling) {
    return (
      <AppLayout title="Zoekprofiel" breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "Zoekprofiel" }]}>
        <div className="max-w-2xl" data-testid={refilling ? "sp-ai-refilling" : "sp-ai-running"}>
          <div className="neuro-card-bold p-8 bg-white">
            <div className="flex items-start gap-4">
              <div className="shrink-0 inline-flex h-14 w-14 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm animate-pulse">
                <Robot size={30} weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#1E88E5] mb-1">
                  Even geduld
                </div>
                <h2 className="font-display text-xl font-bold tracking-tight">
                  {refilling
                    ? "De AI vult je zoekprofiel opnieuw in..."
                    : "De AI vult je zoekprofiel vast in..."}
                </h2>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                  {refilling
                    ? "We nemen je nieuwste testresultaten mee en updaten lege of eerder AI-voor­ingevulde velden. Velden die jij zelf hebt ingevuld blijven onaangetast. Dit duurt ongeveer 30 seconden."
                    : "We combineren alles wat we van je weten — je CV, breintype, IQ-test en werkwaarden — om zoveel mogelijk velden alvast voor je in te vullen. Je kunt daarna alles aanpassen, aanvullen of verwijderen. Dit duurt ongeveer 30 seconden."}
                </p>
                <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                  💡 <strong>Je kunt gerust doorwerken.</strong> Klik in de zijbalk naar een
                  andere pagina — de AI werkt op de achtergrond door en je zoekprofiel staat
                  hier klaar zodra je terugkomt.
                </p>
                <div className="mt-4 flex gap-1">
                  <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }
  if (!profile) {
    return (
      <AppLayout title="Zoekprofiel" breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "Zoekprofiel" }]}>
        <div className="text-zinc-500">Geen profiel gevonden.</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Zoekprofiel"
      breadcrumbs={[{ label: "Profielen", to: "/profielen" }, { label: "Zoekprofiel" }]}
    >
      <div className="max-w-3xl space-y-6 pb-12" data-testid="search-profile-page">
        {/* Intro + completeness */}
        <div className="neuro-card-bold p-5 bg-white">
          <div className="flex items-start gap-3">
            <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
              <MagnifyingGlass size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold tracking-tight">Jouw zoekprofiel</h2>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                Vul samen met je work coach in waar je naar op zoek bent — wat je kunt, wat je
                wilt en waaraan je werkomgeving moet voldoen. Antwoorden worden automatisch
                opgeslagen.
              </p>
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-widest text-zinc-500" data-testid="sp-completeness">
                  <strong className={completeness?.percent === 100 ? "text-[#43A047]" : "text-zinc-900"}>
                    {completeness?.filled || 0}
                  </strong>{" "}
                  / {completeness?.total || 0} velden ingevuld
                  {completeness?.percent === 100 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[#43A047]">
                      <CheckCircle size={14} weight="bold" /> Volledig
                    </span>
                  )}
                </div>
                <SaveIndicator state={saveState} />
              </div>
              {completeness && (
                <div className="mt-2 h-1 bg-zinc-100 rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-[#1E88E5] transition-all"
                    style={{ width: `${completeness.percent}%` }}
                  />
                </div>
              )}
              {profile.ai_refill_available && (
                <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-zinc-600 flex-1 min-w-[200px]">
                    <strong>AI-gebruik: {profile.ai_prefill_count || 0} van {profile.ai_prefill_max || 3}</strong>
                    {(profile.ai_prefill_remaining || 0) > 0 ? (
                      <> — je hebt nog <strong>{profile.ai_prefill_remaining}</strong> keer
                      om de AI je zoekprofiel opnieuw te laten bijwerken. Eigen invulwerk
                      blijft altijd behouden.</>
                    ) : (
                      <> — limiet bereikt. Bewerk de tekst zelf om verder te verfijnen.</>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setRefillConfirm(true)}
                    disabled={(profile.ai_prefill_remaining || 0) <= 0}
                    data-testid="sp-refill-btn"
                    className="inline-flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-zinc-900 hover:text-white transition-colors disabled:border-zinc-300 disabled:text-zinc-400 disabled:hover:bg-white disabled:cursor-not-allowed"
                  >
                    <ArrowsClockwise size={14} weight="bold" /> Opnieuw vullen met AI
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {refillConfirm && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            data-testid="sp-refill-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white max-w-md w-full p-6 rounded-sm border-2 border-zinc-900">
              <div className="flex items-start gap-3 mb-3">
                <WarningCircle size={24} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
                <h3 className="font-display text-lg font-bold tracking-tight">
                  Zoekprofiel opnieuw laten vullen?
                </h3>
              </div>
              <ul className="text-sm text-zinc-700 leading-relaxed space-y-2 mb-3 list-disc pl-5">
                <li>De AI neemt je <strong>nieuwste testresultaten</strong> en CV mee.</li>
                <li>Velden die <strong>jij zelf hebt ingevuld</strong> blijven onaangetast.</li>
                <li>Lege of eerder AI-voor­ingevulde velden worden bijgewerkt.</li>
              </ul>
              <div className="text-xs text-zinc-500 mb-5">
                Na deze run heb je nog <strong>{Math.max(0, (profile?.ai_prefill_remaining || 0) - 1)}</strong> AI-pogingen over.
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setRefillConfirm(false)}
                  data-testid="sp-refill-cancel"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm border-2 border-zinc-300 hover:border-zinc-500 transition-colors"
                >
                  Annuleer
                </button>
                <button
                  type="button"
                  onClick={handleRefill}
                  data-testid="sp-refill-confirm"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm bg-zinc-900 text-white hover:bg-[#1E88E5] transition-colors inline-flex items-center gap-2"
                >
                  <ArrowsClockwise size={14} weight="bold" /> Ja, opnieuw vullen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Werkwaarden hint chip */}
        {workValuesTop.length > 0 && (
          <div className="neuro-card p-4 bg-[#1E88E5]/5 border-[#1E88E5]/30" data-testid="sp-workvalues-hint">
            <div className="flex items-start gap-3">
              <Lightbulb size={20} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#1E88E5] mb-1">Tip uit je werkwaardetest</div>
                <p className="text-sm text-zinc-700 leading-relaxed">
                  Jouw top-3 werkwaarden zijn{" "}
                  <strong>{workValuesTop[0]}</strong>,{" "}
                  <strong>{workValuesTop[1]}</strong> en{" "}
                  <strong>{workValuesTop[2]}</strong>. Verwerk deze in je <em>Motivatie</em>{" "}
                  en bij <em>Voorwaarden werkgever</em> — dat helpt om gericht te solliciteren.
                </p>
              </div>
            </div>
          </div>
        )}

        {profile.ai_prefill_status === "failed" && (
          <div
            className="border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3 rounded-sm flex items-start gap-2"
            data-testid="sp-ai-failed"
          >
            <Sparkle size={18} weight="duotone" className="shrink-0 mt-0.5 text-amber-600" />
            <div>
              De AI kon je zoekprofiel niet volledig automatisch invullen. Geen probleem — de basis­velden zijn voor je voorgevuld; vul de rest zelf in.
            </div>
          </div>
        )}

        {error && (
          <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="sp-error">
            {error}
          </div>
        )}

        {profile.sections.map((section) => (
          <Section
            key={section.id}
            section={section}
            draft={draft}
            onChange={handleChange}
            onBlur={handleBlur}
            prefillSources={prefillSources}
          />
        ))}

        <div className="pt-2">
          <button
            type="button"
            onClick={() => navigate("/profielen")}
            data-testid="sp-back"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
          >
            <CheckCircle size={16} /> Terug naar profielen
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ section, draft, onChange, onBlur, prefillSources }) {
  return (
    <div className="neuro-card-bold p-5 bg-white" data-testid={`sp-section-${section.id}`}>
      <h3 className="font-display text-lg font-bold tracking-tight mb-4 pb-2 border-b border-zinc-100">
        {section.title}
      </h3>
      <div className="space-y-4">
        {section.fields.map((f) => (
          <Field
            key={f.key}
            field={f}
            value={draft[f.key] || ""}
            onChange={(v) => onChange(f.key, v)}
            onBlur={onBlur}
            prefillSource={prefillSources[f.key]}
          />
        ))}
      </div>
    </div>
  );
}

function Field({ field, value, onChange, onBlur, prefillSource }) {
  const isMultiline = field.multiline;
  return (
    <div data-testid={`sp-field-${field.key}`}>
      <label className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs uppercase tracking-widest text-zinc-700 font-semibold">{field.label}</span>
        {prefillSource && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded-sm border border-[#1E88E5]/30" data-testid={`sp-prefill-${field.key}`}>
            <Sparkle size={11} weight="duotone" /> Voor­ingevuld · {prefillSource}
          </span>
        )}
      </label>
      {isMultiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder || ""}
          rows={3}
          className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900 resize-y"
        />
      ) : (
        <input
          type={field.kind === "number" ? "number" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder || ""}
          className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900"
        />
      )}
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full bg-[#1E88E5] animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

function SaveIndicator({ state }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400" data-testid="sp-saving">
        <FloppyDisk size={12} weight="duotone" /> Opslaan...
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#43A047]" data-testid="sp-saved">
        <CheckCircle size={12} weight="bold" /> Opgeslagen
      </span>
    );
  }
  return null;
}
